import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import useMyAccount from "../../../../hooks/useMyAccount";
import { useAuth } from "../../../../hooks/useAuth";
import { API_URLS, DEFAULT_HEADERS } from "../../../../util/constants";
import { handleApiError } from "../../../../util/errorHandler";
import { formatCurrency } from "../../../../util/formatters";
import { buildWhatsAppMessageBuyerDetailContact, generateWhatsAppLink } from "../../../../util/whatsapp";
import { hasImage } from "../../../Products/components/ProductItem";
import KeywordFilter from "../KeywordFilter";

const getMonthRange = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  const toIso = (d) => {
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  };
  return { dateFrom: toIso(start), dateTo: toIso(end) };
};

const BuyerDetails = () => {
  const { companyId, buyerIdUser } = useParams();
  const location = useLocation();

  const { userData, companyData, setOrderDetailId } = useMyAccount();
  const { token } = useAuth();

  const isMasterUser = Boolean(userData?.is_master_user ?? userData?.is_master);

  const [buyer, setBuyer] = useState(() => location?.state?.buyer ?? null);
  const [orders, setOrders] = useState([]);
  const [isLoadingBuyer, setIsLoadingBuyer] = useState(false);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);

  const initialRange = useMemo(() => getMonthRange(), []);
  const [keyword, setKeyword] = useState("");
  const [statusOrder, setStatusOrder] = useState("");
  const [dateFrom, setDateFrom] = useState(initialRange.dateFrom);
  const [dateTo, setDateTo] = useState(initialRange.dateTo);

  const parsedCompanyId = useMemo(() => {
    const n = Number(companyId);
    return Number.isFinite(n) ? n : null;
  }, [companyId]);

  const parsedBuyerIdUser = useMemo(() => {
    const n = Number(buyerIdUser);
    return Number.isFinite(n) ? n : null;
  }, [buyerIdUser]);

  const typeCompanyConnected = useMemo(() => {
    const raw =
      location?.state?.company?.type_company ??
      location?.state?.company?.type_company_connected ??
      null;
    if (raw === null || raw === undefined || raw === "") return null;
    const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
    return Number.isFinite(n) ? n : null;
  }, [location?.state?.company?.type_company, location?.state?.company?.type_company_connected]);

  const canLoad = Boolean(token && parsedCompanyId && parsedBuyerIdUser && isMasterUser);

  const loadBuyerDetails = useCallback(async () => {
    if (!canLoad) return;
    setIsLoadingBuyer(true);
    try {
      const payload = {
        token,
        company_id: parsedCompanyId,
        buyer_id_user: parsedBuyerIdUser,
      };
      if (typeCompanyConnected !== null) payload.type_company_connected = typeCompanyConnected;

      const response = await fetch(API_URLS.BUYER_DETAILS, {
        method: "POST",
        headers: DEFAULT_HEADERS.JSON_WITH_CORS,
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const err = new Error(data?.message || data?.error || "Falha ao carregar detalhes do comprador");
        err.status = response.status;
        err.response = data;
        throw err;
      }
      if (!data?.success) throw new Error(data?.message || data?.error || "Falha ao carregar detalhes do comprador");

      setBuyer(data?.buyer ?? null);
    } catch (error) {
      handleApiError(error, { context: "BuyerDetails.loadBuyerDetails", showToast: false });
    } finally {
      setIsLoadingBuyer(false);
    }
  }, [canLoad, parsedBuyerIdUser, parsedCompanyId, token, typeCompanyConnected]);

  const loadBuyerOrders = useCallback(async () => {
    if (!canLoad) return;
    setIsLoadingOrders(true);
    try {
      const filters = {
        keyword: keyword?.trim() ? keyword.trim() : null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        status_order: statusOrder || null,
      };

      const payload = {
        token,
        company_id: parsedCompanyId,
        buyer_id_user: parsedBuyerIdUser,
        filters,
        pagination: { page: 1, per_page: 100 },
      };
      if (typeCompanyConnected !== null) payload.type_company_connected = typeCompanyConnected;

      const response = await fetch(API_URLS.BUYER_ORDERS, {
        method: "POST",
        headers: DEFAULT_HEADERS.JSON_WITH_CORS,
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const err = new Error(data?.message || data?.error || "Falha ao carregar pedidos do comprador");
        err.status = response.status;
        err.response = data;
        throw err;
      }
      if (!data?.success) throw new Error(data?.message || data?.error || "Falha ao carregar pedidos do comprador");

      setOrders(Array.isArray(data?.orders) ? data.orders : []);
    } catch (error) {
      handleApiError(error, { context: "BuyerDetails.loadBuyerOrders", showToast: false });
      setOrders([]);
    } finally {
      setIsLoadingOrders(false);
    }
  }, [canLoad, dateFrom, dateTo, keyword, parsedBuyerIdUser, parsedCompanyId, statusOrder, token, typeCompanyConnected]);

  useEffect(() => {
    loadBuyerDetails();
  }, [loadBuyerDetails]);

  useEffect(() => {
    loadBuyerOrders();
  }, [loadBuyerOrders]);

  useEffect(() => {
    let isActive = true;
    const loadStatusOptions = async () => {
      try {
        const response = await fetch(API_URLS.STATUS_ORDER_LIST, { method: "GET" });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) return;
        const list = Array.isArray(data?.status_list) ? data.status_list : [];
        if (isActive) setStatusOptions(list);
      } catch {
        // silencioso: filtro é opcional
      }
    };
    loadStatusOptions();
    return () => {
      isActive = false;
    };
  }, []);

  const downloadReportPdfHandler = useCallback(async () => {
    if (!canLoad) return;
    setIsDownloadingPdf(true);
    try {
      const filters = {
        keyword: keyword?.trim() ? keyword.trim() : null,
        date_from: dateFrom || null,
        date_to: dateTo || null,
        status_order: statusOrder || null,
      };

      const payload = {
        token,
        company_id: parsedCompanyId,
        buyer_id_user: parsedBuyerIdUser,
        filters,
      };
      if (typeCompanyConnected !== null) payload.type_company_connected = typeCompanyConnected;

      const response = await fetch(API_URLS.BUYER_ORDERS_REPORT_PDF, {
        method: "POST",
        headers: DEFAULT_HEADERS.JSON_WITH_CORS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(payload?.message || payload?.error || "Falha ao gerar PDF do relatório do comprador");
        err.status = response.status;
        err.response = payload;
        throw err;
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(payload?.message || payload?.error || "Resposta inesperada ao gerar PDF do relatório");
        err.status = response.status;
        err.response = payload;
        throw err;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      handleApiError(error, { context: "BuyerDetails.downloadReportPdfHandler", showToast: false });
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [canLoad, dateFrom, dateTo, keyword, parsedBuyerIdUser, parsedCompanyId, statusOrder, token, typeCompanyConnected]);

  const buyerName = buyer?.full_name || buyer?.name || location?.state?.buyer?.full_name || "Comprador";
  const buyerEmail = buyer?.email ?? location?.state?.buyer?.email ?? null;
  const buyerWhatsapp = buyer?.cellphone ?? buyer?.whatsapp ?? location?.state?.buyer?.cellphone ?? null;
  const buyerImage = buyer?.image ?? location?.state?.buyer?.image ?? null;
  const isBuyerMaster = Boolean(buyer?.is_master ?? location?.state?.buyer?.is_master);

  const buyerWhatsappHref = useMemo(() => {
    if (!buyerWhatsapp) return null;
    const msg = buildWhatsAppMessageBuyerDetailContact({
      senderCompanyName: companyData?.company_name ?? userData?.company_name,
      buyerName,
    });
    return generateWhatsAppLink(buyerWhatsapp, msg);
  }, [buyerName, buyerWhatsapp, companyData?.company_name, userData?.company_name]);

  if (!isMasterUser) {
    return (
      <div className="container">
        <p className="text-muted mb-0">Acesso restrito.</p>
      </div>
    );
  }

  return (
    <div className="container">
      <Link to="/minha-conta/perfil/detalhes-empresa" title="Voltar aos detalhes da empresa">
        <button
          style={{ width: "max-content", marginBottom: "24px", borderRadius: 0 }}
          className="theme-btn-1 btn btn-block"
          type="button"
          title="Voltar"
        >
          <i className="fas fa-arrow-left"></i> Voltar
        </button>
      </Link>

      <div className="ltn-author-introducing clearfix" style={{ width: "100%" }}>
        <div className="author-img">
          <img src={hasImage(buyerImage)} alt="Comprador" />
        </div>
        <div className="author-info">
          <h6>Detalhes do comprador</h6>
          <h2 style={{ color: "#071C1F", marginBottom: "6px" }}>{buyerName}</h2>
          <div className="text-muted" style={{ fontSize: "12px", marginBottom: "10px" }}>
            {isLoadingBuyer ? "Carregando..." : isBuyerMaster ? "Administrador" : "Comprador"}
          </div>

          <div className="footer-address">
            <ul>
              {buyerWhatsapp && buyerWhatsappHref && (
                <li>
                  <div className="footer-address-icon">
                    <i className="fab fa-whatsapp" style={{ color: "#EC1F25" }} />
                  </div>
                  <div className="footer-address-info">
                    <p>
                      <a href={buyerWhatsappHref} target="_blank" rel="noreferrer">
                        {buyerWhatsapp}
                      </a>
                    </p>
                  </div>
                </li>
              )}
              {buyerEmail && (
                <li>
                  <div className="footer-address-icon">
                    <i className="far fa-envelope" style={{ color: "#EC1F25" }} />
                  </div>
                  <div className="footer-address-info">
                    <p>
                      <a href={`mailto:${buyerEmail}`}>{buyerEmail}</a>
                    </p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <div className="d-flex align-items-center justify-content-between flex-wrap" style={{ gap: "12px" }}>
          <h4 className="title-2 mb-0" style={{ color: "#000" }}>
            Pedidos do comprador
          </h4>
          <button
            type="button"
            className="theme-btn-1 btn btn-effect-1"
            onClick={downloadReportPdfHandler}
            disabled={!canLoad || isDownloadingPdf}
            title={isDownloadingPdf ? "Gerando PDF..." : "Baixar PDF do relatório"}
            style={{ padding: "10px 16px" }}
          >
            {isDownloadingPdf ? "Gerando PDF..." : "Baixar PDF"}
          </button>
        </div>

        <KeywordFilter keyword={keyword} onFilterChange={(e) => setKeyword(e.target.value)} />

        <div className="row mt-3" style={{ rowGap: "12px" }}>
          <div className="col-12 col-md-3">
            <label className="form-label" style={{ fontSize: "14px" }}>
              Data inicial
            </label>
            <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label" style={{ fontSize: "14px" }}>
              Data final
            </label>
            <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="col-12 col-md-3">
            <label className="form-label" style={{ fontSize: "14px" }}>
              Status
            </label>
            <select
              className="form-control"
              value={statusOrder}
              onChange={(e) => setStatusOrder(e.target.value)}
              aria-label="Filtrar por status"
            >
              <option value="">Todos os status</option>
              {statusOptions.map((opt, idx) => (
                <option key={String(opt?.status_id ?? idx)} value={opt?.status_id ?? ""}>
                  {opt?.status_name ?? `Status ${opt?.status_id ?? ""}`}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-3 d-flex align-items-end justify-content-end">
            <button
              type="button"
              className="theme-btn-1 btn btn-effect-1"
              onClick={loadBuyerOrders}
              disabled={!canLoad || isLoadingOrders}
              title={isLoadingOrders ? "Carregando..." : "Atualizar lista"}
              style={{ padding: "10px 16px", width: "100%" }}
            >
              {isLoadingOrders ? "Carregando..." : "Atualizar"}
            </button>
          </div>
        </div>

        <div className="ltn__my-properties-table table-responsive col-12 mt-3">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Pedido</th>
                <th scope="col" style={{ textAlign: "center" }}>
                  Data
                </th>
                <th scope="col" style={{ textAlign: "center" }}>
                  Valor
                </th>
                <th scope="col" style={{ textAlign: "center" }}>
                  Visualizar
                </th>
              </tr>
            </thead>
            <tbody style={{ fontSize: "14px" }}>
              {isLoadingOrders ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    Carregando...
                  </td>
                </tr>
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    Nenhum pedido encontrado.
                  </td>
                </tr>
              ) : (
                orders.map((order, idx) => (
                  <tr key={String(order?.id_order ?? idx)}>
                    <td>
                      <h6 className="mb-10 go-top">Pedido N.º {order?.id_order}</h6>
                      {order?.company_name && (
                        <p style={{ fontWeight: "bold" }} className="mb-2">
                          {order.company_name}
                        </p>
                      )}
                      <strong>
                        <i
                          className="fa-solid fa-circle"
                          style={{ color: order?.status_order_color, paddingRight: "8px" }}
                        ></i>
                        Pedido {order?.status_order_name}
                      </strong>
                    </td>
                    <td style={{ textAlign: "center" }}>{order?.order_date}</td>
                    <td style={{ textAlign: "center" }}>
                      {order?.total_price != null && order?.total_price !== ""
                        ? typeof order.total_price === "number"
                          ? formatCurrency(order.total_price)
                          : order.total_price
                        : "—"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Link to="/detalhes-pedido" title="Ver detalhe do pedido">
                        <button
                          style={{ padding: "16px", width: "max-content" }}
                          className="theme-btn-1 btn btn-block"
                          type="button"
                          title="Ver detalhes"
                          onClick={() => setOrderDetailId(order?.id_order)}
                        >
                          <i className="icon-search for-search-show"></i> Ver detalhes
                        </button>
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default BuyerDetails;

